import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MessageCircle, Phone, Star, Filter } from 'lucide-react';
import { filterOptions, businessInfo } from '../data/mockData';

const ProductCard = ({ product }) => {
  const handleTextOrder = () => {
    const message = `Hi! I'm interested in ordering ${product.name} (${product.type}). Can you help me with pricing and availability?`;
    // Use a more compatible SMS format for mobile devices
    window.location.href = `sms:+19455457553?body=${encodeURIComponent(message)}`;
  };

  const handleInquire = () => {
    const message = `Hi! I'd like to know more about ${product.name}. What can you tell me about this strain?`;
    // Use a more compatible SMS format for mobile devices  
    window.location.href = `sms:+19455457553?body=${encodeURIComponent(message)}`;
  };

  return (
    <Card className="group hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden bg-gray-900 border-gray-800 text-white">
      <div className="relative">
        {/* Product Image */}
        <div className="aspect-square bg-gradient-to-br from-gray-800 to-gray-900 relative overflow-hidden">
          {product.image ? (
            <img 
              src={product.image} 
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 filter brightness-110"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-900/30 rounded-full mx-auto mb-2 flex items-center justify-center">
                  <span className="text-2xl">🌿</span>
                </div>
                <p className="text-sm">Product Image</p>
              </div>
            </div>
          )}
          
          {/* Featured Badge */}
          {product.featured && (
            <Badge className="absolute top-3 left-3 bg-green-600 text-white font-bold px-3 py-1">
              Featured
            </Badge>
          )}
          
          {/* Rating */}
          <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
            <Star size={14} className="text-green-400 fill-current" />
            <span className="text-xs font-medium text-white">4.8</span>
          </div>
        </div>
        
        <CardContent className="p-6">
          {/* Product Info */}
          <div className="mb-4">
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-bold text-xl text-white leading-tight">
                {product.name}
              </h3>
            </div>
            
            <p className="text-sm text-green-400 font-semibold mb-3">
              {product.type}
            </p>
            
            {product.tagline && (
              <p className="text-sm italic text-gray-400 mb-3">
                "{product.tagline}"
              </p>
            )}
            
            <p className="text-sm text-gray-300 line-clamp-2 mb-4">
              {product.description}
            </p>
            
            {product.flavor && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 font-medium mb-1">Flavor & Aroma:</p>
                <p className="text-sm text-gray-300">{product.flavor}</p>
              </div>
            )}
            
            {product.bestFor && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 font-medium mb-1">Best For:</p>
                <p className="text-sm text-gray-300">{product.bestFor}</p>
              </div>
            )}
            
            {/* Effects */}
            {product.effects && (
              <div className="flex flex-wrap gap-1 mb-4">
                {product.effects.slice(0, 3).map((effect) => (
                  <Badge key={effect} variant="secondary" className="text-xs bg-green-900/30 text-green-300 border-green-700">
                    {effect}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          {/* Pricing */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
                <div className="font-bold text-green-400">${product.pricing['5g']}</div>
                <div className="text-gray-400">5g</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
                <div className="font-bold text-green-400">${product.pricing['10g']}</div>
                <div className="text-gray-400">10g</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
                <div className="font-bold text-green-400">${product.pricing.half}</div>
                <div className="text-gray-400">Half (14g)</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
                <div className="font-bold text-green-400">${product.pricing.zip}</div>
                <div className="text-gray-400">Zip (28g)</div>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button 
              onClick={handleTextOrder}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium"
            >
              <MessageCircle size={16} className="mr-2" />
              Text to Order
            </Button>
            <Button 
              onClick={handleInquire}
              variant="outline" 
              className="px-4 border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
            >
              Inquire
            </Button>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

const ProductGrid = ({ products, selectedCategory }) => {
  const [filters, setFilters] = useState({
    category: 'all',
    type: 'all',
    featured: 'all'
  });
  
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter products based on all filter criteria
  const filteredProducts = products.filter(product => {
    if (filters.category !== 'all' && product.category !== filters.category) return false;
    if (filters.type !== 'all' && !product.type.toLowerCase().includes(filters.type)) return false;
    if (filters.featured === 'featured' && !product.featured) return false;
    return true;
  });
  
  const handleFilterChange = (filterId, value) => {
    setFilters(prev => ({ ...prev, [filterId]: value }));
  };
  
  return (
    <div className="space-y-6">
      {/* Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <Filter size={16} className="mr-2" />
            Filters
          </Button>
          <span className="text-sm text-gray-400">
            {filteredProducts.length} strain{filteredProducts.length !== 1 ? 's' : ''} found
          </span>
        </div>
      </div>
      
      {/* Filter Dropdowns */}
      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-gray-900 rounded-lg border border-gray-800">
          {filterOptions.map((filterGroup) => (
            <div key={filterGroup.id}>
              <label className="text-sm font-medium text-gray-300 mb-2 block">
                {filterGroup.name}
              </label>
              <Select
                value={filters[filterGroup.id]}
                onValueChange={(value) => handleFilterChange(filterGroup.id, value)}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {filterGroup.options.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-white hover:bg-gray-700">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}
      
      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredProducts.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
      
      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">No strains match your current filters</div>
          <Button 
            onClick={() => setFilters({ category: 'all', type: 'all', featured: 'all' })}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;